import { useState } from "react";
import { useParams } from "react-router-dom";
import { CheckCircle2, ChevronDown, ChevronUp, TrendingUp } from "lucide-react";
import {
  useSession,
  useLogSet,
  useUpdateSet,
  useCompleteSession,
  useSuggestions,
  useUpdateRoutineExercise,
  type SessionExercise,
  type SessionSet,
} from "@/hooks/useApi";

export default function SessionPage() {
  const { id } = useParams<{ id: string }>();
  const { data: session, isLoading } = useSession(id!);
  const logSet = useLogSet(id!);
  const updateSet = useUpdateSet(id!);
  const complete = useCompleteSession(id!);
  const { data: suggestions = [] } = useSuggestions(
    session?.completed_at ? id! : ""
  );
  const updateRoutineEx = useUpdateRoutineExercise();
  const storageKey = `applied-suggestions-${id}`;
  const [appliedSuggestions, setAppliedSuggestions] = useState<Set<string>>(() => {
    try {
      const stored = sessionStorage.getItem(storageKey);
      return stored ? new Set<string>(JSON.parse(stored) as string[]) : new Set<string>();
    } catch { return new Set<string>(); }
  });

  if (isLoading || !session) return <div className="p-4 text-zinc-500 text-sm">Loading…</div>;

  const isCompleted = !!session.completed_at;

  const allSetsLogged =
    session.exercises.length === 0 ||
    session.exercises.every((ex) => {
      const done = session.sets.filter(
        (s) => s.session_exercise_id === ex.id && s.reps_done != null
      ).length;
      return done >= ex.sets_target;
    });

  const pendingSuggestions = suggestions.filter((s) => !appliedSuggestions.has(s.exercise_id));

  return (
    <div className="p-4 space-y-5">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-xl font-semibold">{session.name}</h1>
          <p className="text-xs text-zinc-500">
            {new Date(session.started_at).toLocaleString()}
          </p>
        </div>
        {!isCompleted && allSetsLogged && (
          <button
            onClick={() => complete.mutate(undefined)}
            className="flex items-center gap-1 rounded-lg bg-brand/20 px-3 py-2 text-sm text-brand hover:bg-brand/30 transition-colors"
          >
            <CheckCircle2 className="h-4 w-4" />
            Finish
          </button>
        )}
      </div>

      {pendingSuggestions.length > 0 && (
        <div className="rounded-lg border border-yellow-800/50 bg-yellow-900/20 p-3 space-y-3">
          <p className="text-xs font-medium text-yellow-400 uppercase tracking-wider">
            Ready to progress
          </p>
          {pendingSuggestions.map((s) => (
            <div key={s.exercise_id} className="flex items-center justify-between gap-3">
              <div className="text-sm text-yellow-200">
                <span className="font-medium">{s.exercise_name}</span>
                <span className="text-yellow-400/70"> {s.current_weight}kg → </span>
                <span className="font-semibold">{s.suggest_weight}kg</span>
              </div>
              {s.routine_exercise_id && s.routine_id && (
                <button
                  onClick={() => {
                    updateRoutineEx.mutate({
                      routineId: s.routine_id!,
                      exId: s.routine_exercise_id!,
                      data: { starting_weight_kg: s.suggest_weight },
                    });
                    setAppliedSuggestions((prev) => {
                      const next = new Set([...prev, s.exercise_id]);
                      sessionStorage.setItem(storageKey, JSON.stringify([...next]));
                      return next;
                    });
                  }}
                  className="flex shrink-0 items-center gap-1 rounded-lg bg-yellow-900/60 px-2 py-1 text-xs text-yellow-300 hover:bg-yellow-800/60 transition-colors"
                >
                  <TrendingUp className="h-3 w-3" />
                  Update routine
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      <div className="space-y-4">
        {session.exercises.map((ex) => (
          <ExerciseCard
            key={ex.id}
            exercise={ex}
            sets={session.sets.filter((s) => s.session_exercise_id === ex.id)}
            logSet={logSet.mutate}
            updateSet={updateSet.mutate}
            isCompleted={isCompleted}
          />
        ))}
      </div>

    </div>
  );
}

function ExerciseCard({
  exercise,
  sets,
  logSet,
  updateSet,
  isCompleted,
}: {
  exercise: SessionExercise;
  sets: SessionSet[];
  logSet: (data: {
    session_exercise_id: string;
    set_number: number;
    reps_done: number;
    weight_kg: number;
  }) => void;
  updateSet: (data: { setId: string; data: { reps_done?: number; weight_kg?: number } }) => void;
  isCompleted: boolean;
}) {
  const [expanded, setExpanded] = useState(!isCompleted);
  const [inputs, setInputs] = useState<Record<number, { reps: string; weight: string }>>({});

  const setNums = Array.from({ length: exercise.sets_target }, (_, i) => i + 1);

  function getInput(setNum: number) {
    const logged = sets.find((s) => s.set_number === setNum);
    const defaultWeight = (exercise as SessionExercise & { starting_weight_kg?: number }).starting_weight_kg;
    return inputs[setNum] ?? {
      reps: logged?.reps_done != null ? String(logged.reps_done) : "",
      weight: logged?.weight_kg != null ? String(logged.weight_kg) : defaultWeight ? String(defaultWeight) : "",
    };
  }

  function setInput(setNum: number, field: "reps" | "weight", val: string) {
    setInputs((prev) => ({
      ...prev,
      [setNum]: { ...getInput(setNum), [field]: val },
    }));
  }

  function logOrUpdate(setNum: number) {
    const { reps, weight } = getInput(setNum);
    const repsNum = parseInt(reps);
    const weightNum = parseFloat(weight);
    if (isNaN(repsNum) || isNaN(weightNum)) return;

    const existing = sets.find((s) => s.set_number === setNum);
    if (existing) {
      updateSet({ setId: existing.id, data: { reps_done: repsNum, weight_kg: weightNum } });
    } else {
      logSet({
        session_exercise_id: exercise.id,
        set_number: setNum,
        reps_done: repsNum,
        weight_kg: weightNum,
      });
    }
  }

  const doneCount = sets.filter((s) => s.reps_done != null).length;
  const allDone = doneCount >= exercise.sets_target;

  return (
    <div className="rounded-lg border border-zinc-800">
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex w-full items-center justify-between p-3"
      >
        <div className="text-left">
          <p className="text-sm font-medium">{exercise.exercise_name}</p>
          <p className="text-xs text-zinc-500">
            {exercise.sets_target} × {exercise.min_reps}–{exercise.max_reps} reps
          </p>
        </div>
        <div className="flex items-center gap-2">
          {allDone && <CheckCircle2 className="h-4 w-4 text-green-400" />}
          {expanded ? (
            <ChevronUp className="h-4 w-4 text-zinc-600" />
          ) : (
            <ChevronDown className="h-4 w-4 text-zinc-600" />
          )}
        </div>
      </button>

      {expanded && (
        <div className="border-t border-zinc-800 p-3 space-y-2">
          {setNums.map((setNum) => {
            const logged = sets.find((s) => s.set_number === setNum);
            const isDone = logged?.reps_done != null;

            if (isDone) {
              return (
                <div key={setNum} className="flex items-center gap-3 px-1 py-1">
                  <CheckCircle2 className="h-4 w-4 text-green-400 shrink-0" />
                  <span className="text-sm text-zinc-300">
                    Set {setNum} — <span className="font-medium">{logged.reps_done} reps</span> @ <span className="font-medium">{logged.weight_kg}kg</span>
                  </span>
                </div>
              );
            }

            const input = getInput(setNum);
            return (
              <div key={setNum} className="grid grid-cols-[2rem_1fr_1fr_2.5rem] gap-2 items-center">
                <span className="text-sm font-medium text-zinc-400 text-center">{setNum}</span>
                <input
                  type="number"
                  inputMode="numeric"
                  value={input.reps}
                  onChange={(e) => setInput(setNum, "reps", e.target.value)}
                  placeholder={`${exercise.min_reps}–${exercise.max_reps}`}
                  className="w-full rounded border border-zinc-800 bg-zinc-900 px-2 py-1.5 text-sm text-center focus:outline-none focus:ring-1 focus:ring-zinc-600"
                />
                <input
                  type="number"
                  inputMode="decimal"
                  value={input.weight}
                  onChange={(e) => setInput(setNum, "weight", e.target.value)}
                  placeholder="kg"
                  className="w-full rounded border border-zinc-800 bg-zinc-900 px-2 py-1.5 text-sm text-center focus:outline-none focus:ring-1 focus:ring-zinc-600"
                />
                <button
                  onClick={() => logOrUpdate(setNum)}
                  className="flex items-center justify-center rounded border border-zinc-700 bg-zinc-900 p-1.5 text-zinc-400 hover:border-brand hover:text-brand transition-colors"
                >
                  <CheckCircle2 className="h-4 w-4" />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
